import { useEffect, useMemo } from 'react'

import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { type Control, type UseFormTrigger, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  type PlantUnitFormInput,
  buildPlantUnitFormSchemaWithCrossCheck,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import {
  type OverlapCandidateUnit,
  findPowerTypeOverlaps,
  powerTypeOverlapMessage,
} from '~/pages/AdminPlantUnitMaster/models/unitPowerTypeOverlap'

type PlantLike = { id: string; plantCode: string }

/**
 * 電源ユニットの form の schema。入力中の E1 (同じ系統コードの他のユニットと期間が重なって電源種別が違う) を
 * 電源種別の欄のエラーとして足す。直すまで保存できない。
 *
 * @param units 同じ系統コードの他のユニットを含む、組織のユニット一覧
 * @param selfId 編集中のユニットの id (登録ダイアログは undefined)
 * @param original 編集を始めた時の系統コードと電源種別 (詳細ページの編集だけ。Q9 の例外の判定に使う)
 */
export function usePlantUnitFormSchema(args: {
  units: readonly OverlapCandidateUnit[]
  plants: readonly PlantLike[]
  selfId?: string | undefined
  original?: { plantMasterId: string; powerType: PowerType } | undefined
}): ReturnType<typeof buildPlantUnitFormSchemaWithCrossCheck> {
  const { t } = useTranslation()
  const { units, plants, selfId, original } = args
  const originalPlantId = original?.plantMasterId
  const originalPowerType = original?.powerType
  return useMemo(
    () =>
      buildPlantUnitFormSchemaWithCrossCheck(t, (values) => {
        const overlaps = findPowerTypeOverlaps({
          input: {
            plantMasterId: values.plantMasterId,
            powerType: values.powerType,
            validFrom: values.validFrom,
            validTo: values.validTo,
          },
          selfId,
          original:
            originalPlantId === undefined || originalPowerType === undefined
              ? undefined
              : {
                  plantMasterId: originalPlantId,
                  powerType: originalPowerType,
                },
          units,
        })
        if (overlaps.length === 0) return undefined
        return powerTypeOverlapMessage({
          plantCode:
            plants.find((plant) => plant.id === values.plantMasterId)
              ?.plantCode ?? '',
          overlaps,
        })
      }),
    [t, units, plants, selfId, originalPlantId, originalPowerType],
  )
}

/**
 * 系統コード・電源種別・有効期間のどれかを変えるたびに電源種別の欄を検証し直して、保存を押す前に
 * E1 のエラーを出す (直せば消える)。電源種別が未選択の間は何も出さない。
 */
export function useLivePowerTypeOverlapCheck(
  control: Control<PlantUnitFormInput>,
  trigger: UseFormTrigger<PlantUnitFormInput>,
): void {
  const [plantMasterId, powerType, validFrom, validTo] = useWatch({
    control,
    name: ['plantMasterId', 'powerType', 'validFrom', 'validTo'],
  })
  const key = JSON.stringify([plantMasterId, powerType, validFrom, validTo])
  useEffect(() => {
    if (plantMasterId === '' || powerType === PowerType.UNSPECIFIED) return
    if (validFrom === null || validFrom === undefined) return
    void trigger('powerType')
    // key が 4 つの値の変化を表す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, trigger])
}
